/**
 * Template Version History Page
 *
 * Explores the full version history of a single template: lists all versions,
 * shows a field-level diff between adjacent versions, and allows rollback to
 * an older version via confirmation modal.
 *
 * URL: /templates/:templateId/versions
 *
 * Architecture constraints:
 *   - Mantine v8.x only — no MUI
 *   - Dumb frontend — all data from backend queries
 *   - Types from @/generated/graphql
 *   - useAuth() from @/auth/AuthContext
 *   - Repository pattern via Apollo useQuery / useLazyQuery / useMutation
 *   - No `as any`, no hardcoded field values
 *
 * GraphQL operations used:
 *   Query listTemplateVersions(templateId)          → TemplateVersion[]
 *   Query getTemplateVersionDiff(templateId, from, to) → TemplateVersionDiff
 *   Query rollbackTemplate(input)                   → TemplateRollbackResult
 *     (NB: rollbackTemplate lives on Query root in this schema — it is called
 *      with useLazyQuery, not useMutation)
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  useListTemplateVersionsQuery,
  useGetTemplateVersionDiffQuery,
  useRollbackTemplateLazyQuery,
} from '@/generated/graphql';
import {
  Container,
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Table,
  Button,
  Alert,
  Loader,
  Modal,
  Textarea,
  ThemeIcon,
  Paper,
  ScrollArea,
  Divider,
  Grid,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconHistory,
  IconAlertTriangle,
  IconCheck,
  IconArrowBack,
  IconArrowsExchange,
  IconInfoCircle,
  IconClockHour4,
} from '@tabler/icons-react';
import { useAuth } from '@/auth/AuthContext';
import { useTenantId } from '@/auth';
import type {
  TemplateVersion,
  TemplateVersionDiff,
  TemplateFieldChange,
  TemplateRollbackResult,
  RollbackTemplateInput,
} from '@/generated/graphql';
import { TemplateVersionStatus, TemplateChangeType } from '@/generated/graphql';

// GraphQL operations defined in:
// - queries-part3.graphql: ListTemplateVersions, GetTemplateVersionDiff, RollbackTemplate

// ─── Query Response Interfaces ────────────────────────────────────────────────

interface ListTemplateVersionsQuery {
  listTemplateVersions: TemplateVersion[];
}

interface GetTemplateVersionDiffQuery {
  getTemplateVersionDiff: TemplateVersionDiff;
}

interface RollbackTemplateQuery {
  rollbackTemplate: TemplateRollbackResult;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function statusColor(status: string): string {
  switch (status) {
    case TemplateVersionStatus.ACTIVE:
      return 'green';
    case TemplateVersionStatus.DRAFT:
      return 'blue';
    case TemplateVersionStatus.DEPRECATED:
      return 'orange';
    case TemplateVersionStatus.SUPERSEDED:
      return 'gray';
    default:
      return 'gray';
  }
}

function changeTypeColor(changeType: string): string {
  switch (changeType) {
    case TemplateChangeType.ADDED:
      return 'green';
    case TemplateChangeType.DELETED:
      return 'red';
    case TemplateChangeType.MODIFIED:
      return 'yellow';
    default:
      return 'gray';
  }
}

function formatTimestamp(ts: string): string {
  try {
    return new Date(ts).toLocaleString();
  } catch {
    return ts;
  }
}

function renderJsonValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}

// ─── Rollback Modal ───────────────────────────────────────────────────────────

interface RollbackModalProps {
  opened: boolean;
  onClose: () => void;
  templateId: string;
  tenantId: string;
  targetVersion: number;
  onSuccess: (result: TemplateRollbackResult) => void;
}

function RollbackModal({
  opened,
  onClose,
  templateId,
  tenantId,
  targetVersion,
  onSuccess,
}: RollbackModalProps) {
  const [reason, setReason] = useState('');
  const [rollbackResult, setRollbackResult] = useState<TemplateRollbackResult | null>(null);

  const [executeRollback, { loading, error }] = useRollbackTemplateLazyQuery();

  const handleConfirm = () => {
    const input: RollbackTemplateInput = {
      templateId,
      tenantId,
      targetVersion,
      rollbackReason: reason.trim() || `Rolled back to version ${targetVersion}`,
    };
    void executeRollback({ variables: { input } }).then((result) => {
      const rollback = result.data?.rollbackTemplate;
      if (rollback) {
        setRollbackResult(rollback);
        if (rollback.success) {
          onSuccess(rollback);
        }
      }
    });
  };

  const handleClose = () => {
    setReason('');
    setRollbackResult(null);
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconArrowBack size={18} />
          <Text fw={600}>Rollback to Version {targetVersion}</Text>
        </Group>
      }
      size="md"
    >
      <Stack gap="md">
        {rollbackResult ? (
          rollbackResult.success ? (
            <Alert icon={<IconCheck size={16} />} color="green" title="Rollback Succeeded">
              New version <strong>{rollbackResult.newVersionNumber}</strong> created
              (ID: {rollbackResult.newVersionId}).
            </Alert>
          ) : (
            <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Rollback Failed">
              {rollbackResult.errorMessage ?? 'An unknown error occurred.'}
            </Alert>
          )
        ) : (
          <>
            <Alert icon={<IconAlertTriangle size={16} />} color="orange" variant="light">
              This will create a new version that is a copy of version{' '}
              <strong>{targetVersion}</strong>. The current version is preserved in history.
            </Alert>
            <Textarea
              label="Rollback reason"
              placeholder="Describe why you are rolling back..."
              value={reason}
              onChange={(e) => setReason(e.currentTarget.value)}
              minRows={3}
              autosize
            />
            {error && (
              <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error">
                {error.message}
              </Alert>
            )}
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                color="orange"
                leftSection={loading ? <Loader size={12} color="white" /> : <IconArrowBack size={14} />}
                onClick={handleConfirm}
                disabled={loading}
              >
                Confirm Rollback
              </Button>
            </Group>
          </>
        )}

        {rollbackResult && (
          <Group justify="flex-end">
            <Button variant="subtle" onClick={handleClose}>Close</Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}

// ─── Diff Panel ───────────────────────────────────────────────────────────────

interface DiffPanelProps {
  templateId: string;
  fromVersion: number;
  toVersion: number;
}

function DiffPanel({ templateId, fromVersion, toVersion }: DiffPanelProps) {
  const { data, loading, error } = useGetTemplateVersionDiffQuery({
    variables: { templateId, fromVersion, toVersion },
    skip: fromVersion === toVersion,
  });

  if (loading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
        <Text size="sm" c="dimmed">Loading diff...</Text>
      </Group>
    );
  }

  if (error) {
    return (
      <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Error loading diff">
        {error.message}
      </Alert>
    );
  }

  const diff = data?.getTemplateVersionDiff;
  if (!diff) return null;

  return (
    <Stack gap="sm">
      <Paper withBorder p="sm" radius="sm" bg="var(--mantine-color-blue-0)">
        <Group gap="xs">
          <IconArrowsExchange size={14} />
          <Text size="sm" c="dimmed">
            v{diff.fromVersion} → v{diff.toVersion}:
          </Text>
          <Text size="sm">{diff.summary}</Text>
        </Group>
      </Paper>

      {diff.changes.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No field changes between these versions.
        </Text>
      ) : (
        <Table.ScrollContainer minWidth={500}>
          <Table withTableBorder withColumnBorders fz="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 80 }}>Change</Table.Th>
                <Table.Th>Field</Table.Th>
                <Table.Th>Before</Table.Th>
                <Table.Th>After</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {diff.changes.map((change: TemplateFieldChange, idx: number) => (
                <Table.Tr key={`${change.fieldPath}-${idx}`}>
                  <Table.Td>
                    <Badge
                      size="xs"
                      color={changeTypeColor(change.changeType)}
                      variant="filled"
                    >
                      {change.changeType}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" ff="monospace" fw={500}>{change.fieldPath}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text
                      size="xs"
                      ff="monospace"
                      c={change.changeType === TemplateChangeType.DELETED ? 'red' : 'dimmed'}
                      style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                    >
                      {renderJsonValue(change.oldValue)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text
                      size="xs"
                      ff="monospace"
                      c={change.changeType === TemplateChangeType.ADDED ? 'green' : undefined}
                      style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
                    >
                      {renderJsonValue(change.newValue)}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
    </Stack>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function TemplateVersionPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const tenantId = useTenantId() ?? '';
  useAuth(); // available for scope-gating

  const [selectedVersion, setSelectedVersion] = useState<TemplateVersion | null>(null);
  const [rollbackTarget, setRollbackTarget] = useState<number | null>(null);
  const [rollbackOpened, { open: openRollback, close: closeRollback }] = useDisclosure(false);
  const [rollbackSuccess, setRollbackSuccess] = useState(false);

  const { data, loading, error, refetch } = useListTemplateVersionsQuery({
    variables: { templateId: templateId ?? '' },
    skip: !templateId,
  });

  const versions = data?.listTemplateVersions ?? [];

  // Sort descending by version number so newest is first
  const sortedVersions = [...versions].sort((a, b) => b.versionNumber - a.versionNumber);
  const currentVersion = sortedVersions[0] ?? null;

  const handleVersionClick = (v: TemplateVersion) => {
    setSelectedVersion(v);
  };

  const handleRollback = (v: TemplateVersion) => {
    setRollbackTarget(v.versionNumber);
    openRollback();
  };

  const handleRollbackSuccess = (result: TemplateRollbackResult) => {
    if (result.success) {
      setRollbackSuccess(true);
      void refetch();
    }
  };

  if (!templateId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" title="Missing Template ID">
          No template ID found in URL. Navigate to this page from a template record.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <ThemeIcon size="lg" variant="light" color="blue">
              <IconHistory size={20} />
            </ThemeIcon>
            <div>
              <Title order={2}>Version History</Title>
              <Text size="sm" c="dimmed" ff="monospace">
                Template: {templateId}
              </Text>
            </div>
          </Group>
          {currentVersion && (
            <Badge size="lg" color="green" variant="light">
              Current: v{currentVersion.versionNumber}
            </Badge>
          )}
        </Group>

        {rollbackSuccess && (
          <Alert icon={<IconCheck size={16} />} color="green" title="Rollback applied" withCloseButton onClose={() => setRollbackSuccess(false)}>
            A new version was created. Reloading history.
          </Alert>
        )}

        {/* Two-column layout */}
        <Grid gutter="md">
          {/* Left: Version List */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper withBorder radius="md" p={0} style={{ overflow: 'hidden' }}>
              <Group p="sm" gap="xs" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                <IconClockHour4 size={16} />
                <Text size="sm" fw={600}>All Versions</Text>
                {!loading && (
                  <Badge size="xs" variant="light" color="gray">{sortedVersions.length}</Badge>
                )}
              </Group>

              {loading ? (
                <Group justify="center" py="xl" gap="xs">
                  <Loader size="sm" />
                  <Text size="sm" c="dimmed">Loading versions...</Text>
                </Group>
              ) : error ? (
                <Alert icon={<IconAlertTriangle size={16} />} color="red" m="sm">
                  {error.message}
                </Alert>
              ) : sortedVersions.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="xl" px="sm">
                  No versions found for this template.
                </Text>
              ) : (
                <ScrollArea h={560}>
                  <Stack gap={0}>
                    {sortedVersions.map((v, idx) => {
                      const isSelected = selectedVersion?.versionId === v.versionId;
                      const isCurrent = idx === 0;

                      return (
                        <div key={v.versionId}>
                          <Paper
                            p="sm"
                            radius={0}
                            bg={isSelected ? 'var(--mantine-color-blue-0)' : undefined}
                            style={{
                              cursor: 'pointer',
                              borderLeft: isSelected ? '3px solid var(--mantine-color-blue-5)' : '3px solid transparent',
                            }}
                            onClick={() => handleVersionClick(v)}
                          >
                            <Stack gap={4}>
                              <Group justify="space-between" gap="xs">
                                <Group gap="xs">
                                  <Text size="sm" fw={700}>v{v.versionNumber}</Text>
                                  <Badge
                                    size="xs"
                                    color={statusColor(v.status)}
                                    variant={isCurrent ? 'filled' : 'light'}
                                  >
                                    {v.status}
                                  </Badge>
                                </Group>
                                {!isCurrent && (
                                  <Button
                                    size="compact-xs"
                                    variant="subtle"
                                    color="orange"
                                    leftSection={<IconArrowBack size={10} />}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRollback(v);
                                    }}
                                  >
                                    Rollback
                                  </Button>
                                )}
                              </Group>
                              <Text size="xs" c="dimmed">{formatTimestamp(v.createdAt)}</Text>
                              <Text size="xs" c="dimmed" lineClamp={2}>{v.changeDescription}</Text>
                              <Text size="xs" c="dimmed">by {v.createdBy}</Text>
                            </Stack>
                          </Paper>
                          {idx < sortedVersions.length - 1 && <Divider />}
                        </div>
                      );
                    })}
                  </Stack>
                </ScrollArea>
              )}
            </Paper>
          </Grid.Col>

          {/* Right: Diff View */}
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Paper withBorder radius="md" p={0} style={{ overflow: 'hidden' }}>
              <Group p="sm" gap="xs" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
                <IconArrowsExchange size={16} />
                <Text size="sm" fw={600}>
                  {selectedVersion
                    ? `Changes in v${selectedVersion.versionNumber}`
                    : 'Field Diff'}
                </Text>
              </Group>

              <ScrollArea h={560} p="md">
                {!selectedVersion ? (
                  <Stack justify="center" align="center" h={480}>
                    <Stack align="center" gap="xs">
                      <IconInfoCircle size={32} color="var(--mantine-color-gray-4)" />
                      <Text size="sm" c="dimmed">Select a version to view changes</Text>
                    </Stack>
                  </Stack>
                ) : selectedVersion.previousVersionId == null ? (
                  <Stack gap="sm" p="sm">
                    <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
                      This is the first version of the template. No previous version to diff against.
                    </Alert>
                    <Paper withBorder p="sm" radius="sm">
                      <Stack gap="xs">
                        <Group gap="xs">
                          <Text size="xs" fw={600} w={120}>Version:</Text>
                          <Text size="xs">{selectedVersion.versionNumber}</Text>
                        </Group>
                        <Group gap="xs">
                          <Text size="xs" fw={600} w={120}>Created at:</Text>
                          <Text size="xs">{formatTimestamp(selectedVersion.createdAt)}</Text>
                        </Group>
                        <Group gap="xs">
                          <Text size="xs" fw={600} w={120}>Created by:</Text>
                          <Text size="xs">{selectedVersion.createdBy}</Text>
                        </Group>
                        <Group gap="xs">
                          <Text size="xs" fw={600} w={120}>Description:</Text>
                          <Text size="xs">{selectedVersion.changeDescription}</Text>
                        </Group>
                      </Stack>
                    </Paper>
                  </Stack>
                ) : (
                  <Stack gap="sm" p="sm">
                    <Paper withBorder p="sm" radius="sm">
                      <Stack gap="xs">
                        <Group gap="xs">
                          <Text size="xs" fw={600} w={120}>Created at:</Text>
                          <Text size="xs">{formatTimestamp(selectedVersion.createdAt)}</Text>
                        </Group>
                        <Group gap="xs">
                          <Text size="xs" fw={600} w={120}>Created by:</Text>
                          <Text size="xs">{selectedVersion.createdBy}</Text>
                        </Group>
                        <Group gap="xs">
                          <Text size="xs" fw={600} w={120}>Description:</Text>
                          <Text size="xs">{selectedVersion.changeDescription}</Text>
                        </Group>
                      </Stack>
                    </Paper>

                    <DiffPanel
                      templateId={templateId}
                      fromVersion={selectedVersion.versionNumber - 1}
                      toVersion={selectedVersion.versionNumber}
                    />
                  </Stack>
                )}
              </ScrollArea>
            </Paper>
          </Grid.Col>
        </Grid>
      </Stack>

      {/* Rollback Modal */}
      {rollbackTarget !== null && (
        <RollbackModal
          opened={rollbackOpened}
          onClose={closeRollback}
          templateId={templateId}
          tenantId={tenantId}
          targetVersion={rollbackTarget}
          onSuccess={handleRollbackSuccess}
        />
      )}
    </Container>
  );
}

export default TemplateVersionPage;
